// src/components/LoginPage.js
import React, { useState } from "react";
import apiService from "../services/ApiService";
import {
  Box,
  Link,
  VStack,
  HStack,
  Heading,
  Image,
  Checkbox,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import FormInput from "./FormInput";
import { faKey, faUser } from "@fortawesome/free-solid-svg-icons";
import * as IconButton from "../themes/IconButtonSet";
import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
  const navigate = useNavigate();

  const titleFontSize = "clamp(0.7rem, 1.8vw, 0.9rem)";
  const bodyFontSize = "clamp(0.6rem, 1.8vw, 0.8rem)";
  const smFontSize = "clamp(0.5rem, 1.8vw, 0.7rem)";
  const toast = useToast();
  const { login } = useAuth();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const base64_encode = (str) => btoa(unescape(encodeURIComponent(str)));
  const base64_decode = (str) => decodeURIComponent(escape(atob(str)));

  const encLoginData = (user, password) => {
    const loginValue = { user, password };
    const pos = Math.floor(Math.random() * 6) + 3;
    const schar = Math.floor(Math.random() * 9);
    const encoded = base64_encode(JSON.stringify(loginValue));
    return encoded.slice(0, pos) + schar + encoded.slice(pos) + pos;
  };

  const decLoginData = (token) => {
    try {
      const [header, payload] = token.split(".");
      if (!payload) throw new Error("Invalid JWT token");
      const decodedPayload = base64_decode(payload);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("Token decode error:", error);
      throw new Error("Failed to decode token");
    }
  };

  const handleLogin = async () => {
    if (!userName || !password) {
      toast({
        title: "กรุณากรอกชื่อผู้ใช้และรหัสผ่านให้ครบถ้วน",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!isTermsAccepted) {
      toast({
        title: "กรุณายอมรับเงื่อนไขการใช้งานก่อน",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const apiPath = "/getUserLoginInfo";
      const postData = { userauthen: encLoginData(userName, password) };

      //console.log("LOGIN DATA:", JSON.stringify(postData));
      const response = await apiService({
        method: "POST",
        url: apiPath,
        data: postData,
        sessionId: "",
      });
      //console.log("API Response:", JSON.stringify(response));

      if (response.result === "success") {
        login({
          sessionId: response.token,
          login: true,
          profile: response.Profile?.[0] || {},
          auth: response.Authorization?.[0] || {},
        });

        toast({
          title: "Successfully logged in!",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
        //console.log("User Profile:", JSON.stringify(response.Profile[0]));
        if (response.Profile[0].OfficeID) {
          navigate("/dashboard");
        } else {
          navigate("/home");
        }
      } else {
        console.log("Login Failed with response:", JSON.stringify(response));
        toast({
          title: response.error || "Login failed",
          description: "กรุณาลองใหม่อีกครั้ง",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Login Error:", error);
      toast({
        title: "Login failed: " + (error.message || "Unknown error"),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      maxW={{ base: "100%", md: "xl", lg: "4xl" }}
      mt={{ base: 15, md: 10, lg: 35 }}
      mb={{ base: 15, md: 10, lg: 30 }}
      mx="auto"
      borderWidth={1}
      borderRadius="2xl"
      boxShadow="lg"
      h="max-content"
      overflow="hidden"
    >
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}>
        <GridItem
          display={{ base: "none", md: "block" }}
          backgroundImage="url('/bglogin.png')"
          backgroundSize="cover"
          backgroundPosition="center"
        />
        <GridItem
          bg="white"
          p={{ base: 4, md: 6 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack
            spacing={{ base: 1, md: 3 }}
            w={{ base: "100%", md: "80%" }}
            maxW="400px"
          >
            <VStack spacing={{ base: 3, md: 5 }} align="center">
              <Image
                src="/logo.png"
                alt="Logo"
                boxSize={{ base: "40px", md: "50px" }}
              />
              <VStack spacing={1} align="center">
                <Heading
                  fontSize={titleFontSize}
                  fontWeight="semibold"
                  color="#3b7072"
                  textAlign="center"
                >
                  สำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์
                </Heading>
                <Heading
                  fontSize={titleFontSize}
                  fontWeight="semibold"
                  color="#3b7072"
                  textAlign="center"
                >
                  Bureau of Veterinary Biologics
                </Heading>
              </VStack>
            </VStack>
            <VStack spacing={{ base: 1, md: 3 }} w="100%">
              <FormInput
                size="sm"
                fontSize={bodyFontSize}
                label="ชื่อผู้ใช้งาน"
                type="text"
                placeholder=""
                name="username"
                icon={faUser}
                value={userName}
                colorScheme="iBlue"
                onChange={(e) => setUserName(e.target.value)}
              />
              <FormInput
                size="sm"
                fontSize={bodyFontSize}
                label="รหัสผ่าน"
                type="password"
                placeholder=""
                name="password"
                icon={faKey}
                value={password}
                colorScheme="iBlue"
                onChange={(e) => setPassword(e.target.value)}
              />
              <Box mt={{ base: 2, md: 3 }} mb={{ base: 2, md: 3 }}>
                <Checkbox
                  isChecked={isTermsAccepted}
                  onChange={(e) => setIsTermsAccepted(e.target.checked)}
                  size={{ base: "sm", md: "md" }}
                >
                  <Box fontSize={smFontSize}>
                    ยอมรับเงื่อนไขการใช้งาน รวมถึงนโยบายความเป็นส่วนตัว
                    และยอมรับการใช้งานของคุกกี้ และเทคโนโลยีต่างๆ ที่เกี่ยวข้อง
                  </Box>
                </Checkbox>
              </Box>
              <IconButton.Login onClick={handleLogin} w="100%" size="sm" />
              
              <Link
                href="https://forms.gle/3vLA5WrQrYetRDoDA"
                target="_blank"
                color="teal.500"
                fontSize={bodyFontSize}
              >
              <Image
                src="banner/VHServiceQuestionair.png"
                alt="BannerQuestionair"
                w="100%"
                h="100%"
                objectFit="cover"
                objectPosition="center"
                borderRadius="lg"
                boxShadow="lg"
                mb={4}
              />
              </Link>
              
              <Link href="#" color="teal.500" fontSize={bodyFontSize}>
                ลืมรหัสผ่าน
              </Link>
              <Link
                href="/registrationfarmer"
                color="teal.500"
                fontSize={bodyFontSize}
              >
                ลงทะเบียนเพื่อใช้งานระบบ
              </Link>
              <Link
                href="/videos/VaccineReservation.mp4"
                target="_blank"
                color="teal.500"
                fontSize={bodyFontSize}
              >
                วิดีโอสาธิตการใช้งานระบบสั่งจองวัคซีนออนไลน์
              </Link>
            </VStack>
          </VStack>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default LoginPage;
